import React from 'react';

import { useMutation, useQuery } from '@apollo/react-hooks';
import { Box, debounce, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';

import { CustomButton } from '../../../../components/UI/CustomButton';
import { ADD_LEARNER_TO_TEAM, GET_COMPANY_LEARNERS } from '../../../../graphql/user';
import { Mutation, Query } from '../../../../types';
import useValidation from '../../../../utils/useValidation';
import useStyles from './styles';
import { inviteValidationSchema } from './validation';

const InviteExistingLearner = ({ teamId, onSuccess }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const { values, errors, handleSubmit, setValue, resetToDefault } = useValidation(
    {
      learner: null,
      email: '',
    },
    inviteValidationSchema,
    onSubmit,
  );

  const { data: companyLearnersData } = useQuery<Pick<Query, 'getCompanyLearners'>>(
    GET_COMPANY_LEARNERS,
    {
      variables: { team_id: teamId, learner_email: values.email },
      skip: !Boolean(values.email.length),
    },
  );

  const [addToTeam] = useMutation<Pick<Mutation, 'addLearnerToTeam'>>(ADD_LEARNER_TO_TEAM, {
    onCompleted: () => {
      resetToDefault();
      onSuccess();
    },
  });

  function onSubmit({ learner }) {
    addToTeam({
      variables: {
        team_id: teamId,
        learner_id: +learner.id,
      },
    });
  }

  const handleInputChange = debounce((e, v) => setValue('email', v), 500);
  const handleAutocompleteChange = (e, v) => setValue('learner', v);

  return (
    <Box className={classes.fullWidthContainer}>
      <Autocomplete
        options={companyLearnersData?.getCompanyLearners || []}
        getOptionLabel={(option) => option?.email || ''}
        renderInput={(params) => (
          <TextField
            {...params}
            name="learner"
            variant="outlined"
            label={t('input.label.email')}
            error={Boolean(errors?.learner)}
            helperText={errors?.learner}
          />
        )}
        value={values.learner}
        onInputChange={handleInputChange}
        onChange={handleAutocompleteChange}
      />
      <CustomButton onClick={handleSubmit} btnClass={classes.btn}>
        {t('button.add')}
      </CustomButton>
    </Box>
  );
};

export default InviteExistingLearner;
